import React, { FormEvent, useCallback, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  type AddLocationRequest,
  type Location,
  addLocation,
  deleteLocationImage,
  getLocations,
} from '~/services/TripPlannerService';

import ImageLinkWithDelete from './ImageLinkWithDelete';
import LocationImageUploader from './LocationImageUploader';

export default function TripPlannerImagesForSuggestedPlaces() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(0);
  const [searchDestinationId, setSearchDestinationId] = useState('');
  const [searchLocationName, setSearchLocationName] = useState('');

  const [newLocation, setNewLocation] = useState<AddLocationRequest>({
    destinationId: '',
    imageId: '',
  });

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    destinationId: string;
    imageId: string;
  }>({
    open: false,
    destinationId: '',
    imageId: '',
  });

  const {
    data: locations = [],
    isLoading,
    error,
  } = useQuery<Array<Location>>({
    queryKey: ['locations', page, searchDestinationId, searchLocationName],
    queryFn: async () => {
      const response = await getLocations({
        page: String(page + 1),
        ...(searchDestinationId && { destinationId: searchDestinationId }),
        ...(searchLocationName && { locationName: searchLocationName }),
      });

      if (response.status === 200) {
        return response.result;
      }
      throw new Error('Failed to fetch locations');
    },
  });

  const addLocationMutation = useMutation({
    mutationFn: (data: AddLocationRequest) => addLocation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setNewLocation({ destinationId: '', imageId: '' });
      enqueueSnackbar('Image added to location successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to add image: ${error.message}`, { variant: 'error' });
    },
  });

  const deleteImageMutation = useMutation({
    mutationFn: ({ destinationId, imageId }: { destinationId: string; imageId: string }) =>
      deleteLocationImage(destinationId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      enqueueSnackbar('Image deleted successfully', { variant: 'success' });
      setDeleteDialog({ open: false, destinationId: '', imageId: '' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to delete image: ${error.message}`, { variant: 'error' });
    },
  });

  const handleAddLocation = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      addLocationMutation.mutate(newLocation);
    },
    [addLocationMutation, newLocation],
  );

  const handleSearch = useCallback((e: FormEvent) => {
    e.preventDefault();
    setPage(0);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchDestinationId('');
    setSearchLocationName('');
    setPage(0);
  }, []);

  const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const destinationId = event.currentTarget.dataset.destinationId;
    const imageId = event.currentTarget.dataset.imageId;

    if (destinationId && imageId) {
      setDeleteDialog({ open: true, destinationId, imageId });
    }
  }, []);

  const handleConfirmDelete = useCallback(() => {
    deleteImageMutation.mutate({
      destinationId: deleteDialog.destinationId,
      imageId: deleteDialog.imageId,
    });
  }, [deleteImageMutation, deleteDialog.destinationId, deleteDialog.imageId]);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDeleteDialog({ open: false, destinationId: '', imageId: '' });
  }, []);

  const handleDestinationIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewLocation((prev) => ({ ...prev, destinationId: e.target.value }));
  }, []);

  const handleImageIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewLocation((prev) => ({ ...prev, imageId: e.target.value }));
  }, []);

  const handleSearchDestinationIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchDestinationId(e.target.value);
  }, []);

  const handleSearchLocationNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchLocationName(e.target.value);
  }, []);

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Error loading locations: {error.message}</Alert>
        </Box>
      );
    }

    return (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Location Name</TableCell>
                <TableCell>Destination ID</TableCell>
                <TableCell>Images</TableCell>
                <TableCell align="right">Upload</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No locations found
                  </TableCell>
                </TableRow>
              ) : (
                locations.map((location) => (
                  <TableRow key={location.destinationId}>
                    <TableCell>{location.locationName}</TableCell>
                    <TableCell>{location.destinationId}</TableCell>
                    <TableCell>
                      <Stack direction="column" spacing={0.5} alignItems="flex-start">
                        {location.locationImageIds.map((imageId) => (
                          <ImageLinkWithDelete
                            key={imageId}
                            imageId={imageId}
                            destinationId={location.destinationId}
                            onDelete={handleDeleteClick}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <LocationImageUploader
                        destinationId={location.destinationId}
                        locationName={location.locationName}
                      />
                    </TableCell>
                    <TableCell>{new Date(location.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(location.updatedAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={-1}
          rowsPerPage={10}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to }) => `${from}–${to}`}
        />
      </>
    );
  };

  return (
    <Container maxWidth="xl">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            Add Image to Location
          </Typography>
          <Box component="form" onSubmit={handleAddLocation}>
            <Stack spacing={2}>
              <TextField
                label="Destination ID"
                required
                fullWidth
                value={newLocation.destinationId}
                onChange={handleDestinationIdChange}
                placeholder="Enter destination ID"
              />
              <TextField
                label="Image ID"
                required
                fullWidth
                value={newLocation.imageId}
                onChange={handleImageIdChange}
                placeholder="Enter image ID"
              />
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={addLocationMutation.isPending || !newLocation.destinationId || !newLocation.imageId}
                >
                  {addLocationMutation.isPending ? <CircularProgress size={24} /> : 'Add Image'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>

        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            Search Locations
          </Typography>
          <Box component="form" onSubmit={handleSearch}>
            <Stack spacing={2}>
              <TextField
                label="Search by Destination ID"
                fullWidth
                value={searchDestinationId}
                onChange={handleSearchDestinationIdChange}
                placeholder="Enter destination ID"
              />
              <TextField
                label="Search by Location Name"
                fullWidth
                value={searchLocationName}
                onChange={handleSearchLocationNameChange}
                placeholder="Enter location name"
              />
              <Stack direction="row" spacing={2}>
                <Button type="submit" variant="contained">
                  Search
                </Button>
                <Button
                  variant="text"
                  onClick={handleClearSearch}
                  disabled={!searchDestinationId && !searchLocationName}
                >
                  Clear
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Stack>

      <Paper>{renderTableContent()}</Paper>

      <Dialog open={deleteDialog.open} onClose={handleCloseDialog}>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this image? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={deleteImageMutation.isPending}>
            {deleteImageMutation.isPending ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
